/* eslint-disable @typescript-eslint/no-unused-vars */
import { AddSharp } from "@mui/icons-material";
import { Button } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UseDebounce } from "../../../states/Hooks/UseDebounce";
import { useLoading } from "../../../views/Loader/LoadingContext";
import SideBarNavigation from "../../Layouts/Sidebar/Sidebar";
import OrderTableComponent from "../../Layouts/Table/OrderTableComponent";
import CustomDropdown from "../../UI/CustomDropdown/CustomDropdown";

const statusOptions = [
  { value: "", label: "ALL" },
  { value: "DRAFT", label: "Draft" },
  { value: "CREATED", label: "Created" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELED", label: "Cancelled" },
];

function PurchaseOrderPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setLoading } = useLoading();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(
    () => searchParams.get("status") || ""
  );
  const debouncedSearch = UseDebounce(search, 200);

  // Update URL when status filter changes
  useEffect(() => {
    if (statusFilter) {
      setSearchParams({ status: statusFilter });
    } else {
      setSearchParams({});
    }
  }, [statusFilter, setSearchParams]);

  const userAuthority = localStorage.getItem("authority");
  const isInventoryManager = userAuthority === "ROLE_INVENTORY_MANAGER";

  const handleCreateOrder = () => {
    navigate(`/create-order${statusFilter ? `?status=${statusFilter}` : ''}`);
  };

  const handleEditOrder = (orderId: number) => {
    navigate(`/purchase-orders/edit/${orderId}${statusFilter ? `?status=${statusFilter}` : ''}`);
  };

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  return (
    <div className="flex h-screen bg-gray-50">
      <div className="w-64 bg-white shadow-lg">
        <SideBarNavigation />
      </div>

      <div className="flex-1 p-6 ml-6 overflow-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-semibold text-gray-900">
            Purchase Orders
          </h1>
          {!isInventoryManager && (
            <Button
              variant="contained"
              startIcon={<AddSharp />}
              onClick={handleCreateOrder}
              sx={{
                textTransform: "none",
                bgcolor: "#1f2937",
                "&:hover": { bgcolor: "#111827" },
                fontSize: "14px",
                borderRadius: "32px",
                px: 4,
                py: 1.5,
                fontWeight: 600,
              }}>
              Add Order
            </Button>
          )}
        </div>

        {/* Filters and Search */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-4">
            <CustomDropdown
              label="Status:"
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              minWidth="130px"
            />
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by order number or parts"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-80 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <OrderTableComponent
            onEditOrder={handleEditOrder}
            currentPage={page}
            setCurrentPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            search={debouncedSearch}
            statusFilter={statusFilter}
          />
        </div>
      </div>
    </div>
  );
}

export default PurchaseOrderPage;
